import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, Navigate } from 'react-router'


import { AuthLayout } from './AuthLayout'
import { Field, FormError, SubmitButton } from './Field'

import { useSignUp } from '@/lib/auth/queries'

const MIN_PASSWORD_LENGTH = 12

export function SignUpPage() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [accountType, setAccountType] = useState<'business' | 'reseller'>('business')
  const [businessName, setBusinessName] = useState('')
  const [phone, setPhone] = useState('')
  const signUp = useSignUp()
  const isReseller = accountType === 'reseller'

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (passwordTooShort) return
    signUp.mutate({
      email,
      password,
      fullName,
      ...(isReseller ? { reseller: { businessName: businessName.trim(), phone: phone.trim() } } : {}),
    })
  }

  // A reseller is already signed in: the app shows "waiting for approval".
  if (signUp.isSuccess && isReseller) return <Navigate to="/" replace />

  // Email verification is REQUIRED before a workspace can be created.
  if (signUp.isSuccess) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`We sent a confirmation link to ${email}.`}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Click the link to confirm your address, then sign in. The link expires in
          24 hours.
        </p>
        <p
          style={{
            marginTop: 'var(--spacing-4)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Running locally? Mail is captured at{' '}
          <a href="http://localhost:54324">localhost:54324</a>.
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Turn every website visitor into a conversation."
      footer={
        <>
          Already have an account? <Link to="/auth/signin">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {signUp.error && <FormError message={signUp.error.message} />}

        <fieldset
          disabled={signUp.isPending}
          style={{ border: 'none', padding: 0, margin: '0 0 var(--spacing-4)' }}
        >
          <legend
            style={{
              fontSize: 'var(--text-compact)',
              fontWeight: 500,
              marginBottom: 'var(--spacing-2)',
            }}
          >
            I am signing up as
          </legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-2)' }}>
            {(
              [
                ['business', 'A business', 'I want to use Convo for my company.'],
                ['reseller', 'A reseller', 'I will set Convo up for client companies.'],
              ] as const
            ).map(([value, title, text]) => {
              const selected = accountType === value
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setAccountType(value)}
                  style={{
                    textAlign: 'left',
                    padding: 'var(--spacing-3)',
                    borderRadius: 'var(--radius-control)',
                    border: `1px solid ${selected ? 'var(--color-action)' : 'var(--color-border-strong)'}`,
                    background: selected ? 'var(--color-action-subtle)' : 'transparent',
                    minHeight: 44,
                  }}
                >
                  <span style={{ display: 'block', fontWeight: 600, fontSize: 'var(--text-compact)' }}>
                    {title}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-caption)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {text}
                  </span>
                </button>
              )
            })}
          </div>
        </fieldset>

        <Field
          label="Full name"
          value={fullName}
          onChange={setFullName}
          autoComplete="name"
          required
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          disabled={signUp.isPending}
        />
        <Field
          label="Work email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          disabled={signUp.isPending}
        />
        {isReseller && (
          <>
            <Field
              label="Business name"
              value={businessName}
              onChange={setBusinessName}
              autoComplete="organization"
              required
              disabled={signUp.isPending}
            />
            <Field
              label="Phone"
              type="tel"
              value={phone}
              onChange={setPhone}
              autoComplete="tel"
              hint="Convo may call to verify your business before approving."
              disabled={signUp.isPending}
            />
          </>
        )}
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          error={passwordTooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : undefined}
          disabled={signUp.isPending}
        />

        <SubmitButton
          loading={signUp.isPending}
          disabled={passwordTooShort || (isReseller && businessName.trim().length < 2)}
        >
          {isReseller ? 'Apply as a reseller' : 'Create account'}
        </SubmitButton>
      </form>
    </AuthLayout>
  )
}
